(() => {
  "use strict";

  const sourceScript = document.currentScript;
  const clockUrl = sourceScript && sourceScript.dataset.clockUrl;
  const lottieUrl = sourceScript && sourceScript.dataset.lottieUrl;
  if (!clockUrl) return;

  let pageLoader = null;
  let pageAnimation = null;
  let inlineClock = null;
  let inlineAnimation = null;
  let inlineHost = null;
  let inlineMountPending = false;
  let inlineShowTimer = 0;
  let lottiePromise = null;
  let pageShowTimer = 0;
  let pageHideTimer = 0;
  let safetyTimer = 0;
  let loadingDepth = 0;
  let syncTimer = 0;
  const loadMoreSafetyTimers = new WeakMap();
  const actionSafetyTimers = new WeakMap();
  const actionFinishTimers = new WeakMap();
  const actionStartedAt = new WeakMap();
  const cardActionSelector = [
    ".hdt-card-product__btn-quick-view",
    ".hdt-card-product__btn-ultra"
  ].join(",");

  const busySelector = [
    '[aria-busy="true"]',
    '[data-bt-action-pending="true"]',
    ".hdt-pagination-wrapper[loading]",
    ".bt-watch-review-submit.is-loading",
    "[data-bt-watch-cart].is-loading"
  ].join(",");

  function createClock(className) {
    const element = document.createElement("span");
    element.className = className;
    element.setAttribute("aria-hidden", "true");
    return element;
  }

  function normalizeInlineClockSvg(element) {
    if (!element || !element.classList.contains("bt-clock-canvas--inline")) return;
    const svg = element.querySelector("svg");
    if (!svg) return;

    /* Product-card rules also size their normal plus/eye SVG. Inline styles
       keep those rules from shrinking the Lottie canvas after it is mounted. */
    svg.style.setProperty("display", "block", "important");
    svg.style.setProperty("width", "100%", "important");
    svg.style.setProperty("height", "100%", "important");
    svg.style.setProperty("max-width", "none", "important");
    svg.style.setProperty("max-height", "none", "important");
    svg.style.setProperty(
      "transform",
      "scale(var(--bt-clock-visual-scale, 1.45))",
      "important"
    );
    svg.style.setProperty("transform-origin", "center", "important");
  }

  function ensureLottie() {
    if (window.lottie && typeof window.lottie.loadAnimation === "function") {
      return Promise.resolve(window.lottie);
    }
    if (!lottieUrl) return Promise.reject(new Error("Lottie URL is unavailable"));
    if (lottiePromise) return lottiePromise;

    lottiePromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-bt-lottie-runtime]');
      const script = existing || document.createElement("script");
      const done = () => {
        if (window.lottie && typeof window.lottie.loadAnimation === "function") resolve(window.lottie);
        else reject(new Error("Lottie runtime failed to initialize"));
      };
      script.addEventListener("load", done, { once: true });
      script.addEventListener("error", reject, { once: true });
      if (!existing) {
        script.src = lottieUrl;
        script.defer = true;
        script.dataset.btLottieRuntime = "";
        document.head.appendChild(script);
      }
    }).catch((error) => {
      lottiePromise = null;
      throw error;
    });
    return lottiePromise;
  }

  async function mountClock(element) {
    if (!element) return null;
    try {
      const lottie = await ensureLottie();
      if (!element.isConnected) return null;
      const animation = lottie.loadAnimation({
        container: element,
        renderer: "svg",
        loop: true,
        autoplay: false,
        path: clockUrl,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid meet",
          progressiveLoad: true
        }
      });
      normalizeInlineClockSvg(element);
      if (animation && typeof animation.addEventListener === "function") {
        animation.addEventListener("DOMLoaded", () => normalizeInlineClockSvg(element));
      }
      return animation;
    } catch (error) {
      return null;
    }
  }

  function ensurePageLoader() {
    if (pageLoader) return;

    pageLoader = document.createElement("div");
    pageLoader.className = "bt-global-clock-loader";
    pageLoader.hidden = true;
    pageLoader.setAttribute("aria-hidden", "true");

    const panel = document.createElement("span");
    panel.className = "bt-global-clock-loader__panel";
    const clock = createClock("bt-clock-canvas");
    panel.appendChild(clock);
    pageLoader.appendChild(panel);
    document.body.appendChild(pageLoader);
    mountClock(clock).then((animation) => {
      pageAnimation = animation;
      if (pageAnimation && pageLoader && !pageLoader.hidden) pageAnimation.play();
    });
  }

  function showPageLoader() {
    loadingDepth += 1;
    window.clearTimeout(pageShowTimer);
    window.clearTimeout(pageHideTimer);
    window.clearTimeout(safetyTimer);

    pageShowTimer = window.setTimeout(() => {
      if (loadingDepth <= 0) return;
      ensurePageLoader();
      pageLoader.hidden = false;
      pageLoader.classList.remove("is-leaving");
      if (pageAnimation) pageAnimation.play();
    }, 160);

    safetyTimer = window.setTimeout(() => {
      loadingDepth = 0;
      hidePageLoader(true);
    }, 12000);
  }

  function hidePageLoader(force, immediate) {
    loadingDepth = force ? 0 : Math.max(0, loadingDepth - 1);
    if (loadingDepth > 0) return;

    window.clearTimeout(pageShowTimer);
    window.clearTimeout(pageHideTimer);
    window.clearTimeout(safetyTimer);
    if (!pageLoader) return;
    if (immediate) {
      pageLoader.classList.remove("is-leaving");
      pageLoader.hidden = true;
      if (pageAnimation) pageAnimation.pause();
      return;
    }

    pageLoader.classList.add("is-leaving");
    pageHideTimer = window.setTimeout(() => {
      if (loadingDepth === 0) {
        pageLoader.hidden = true;
        if (pageAnimation) pageAnimation.pause();
      }
    }, 80);
  }

  function ensureInlineClock() {
    if (!inlineClock) {
      inlineClock = createClock("bt-clock-canvas bt-clock-canvas--inline");
      inlineClock.hidden = true;
      document.body.appendChild(inlineClock);
    }

    if (!inlineAnimation && !inlineMountPending) {
      inlineMountPending = true;
      mountClock(inlineClock).then((animation) => {
        inlineAnimation = animation;
        inlineMountPending = false;
        if (!inlineAnimation) return;
        document.documentElement.classList.add("bt-clock-loader-ready");
        if (inlineHost && inlineHost.isConnected && !inlineClock.hidden) inlineAnimation.play();
      });
    }
  }

  function resolveBusyHost(element) {
    if (!element) return null;
    const actionHost = element.matches('[data-bt-action-clock-host="true"]')
      ? element
      : element.closest('[data-bt-action-clock-host="true"]');
    if (actionHost) return actionHost;
    if (element.matches(".hdt-pagination-wrapper")) {
      return element.querySelector("a.hdt-loamore-btn");
    }
    if (element.matches("input, select, textarea")) {
      return element.closest("button, a, form");
    }
    return element;
  }

  function clearInlineClock() {
    window.clearTimeout(inlineShowTimer);
    inlineShowTimer = 0;
    if (inlineHost) inlineHost.classList.remove("bt-clock-active-host");
    inlineHost = null;
    if (!inlineClock) return;
    if (!inlineClock.hidden) inlineClock.hidden = true;
    if (inlineAnimation) inlineAnimation.pause();
    if (inlineClock.parentElement !== document.body) {
      document.body.appendChild(inlineClock);
    }
  }

  function showInlineClock(host, immediate) {
    if (!host || !host.isConnected) {
      clearInlineClock();
      return;
    }

    if (
      inlineHost === host &&
      inlineClock &&
      !inlineClock.hidden &&
      inlineClock.parentElement === host
    ) return;
    window.clearTimeout(inlineShowTimer);
    if (inlineHost && inlineHost !== host) {
      inlineHost.classList.remove("bt-clock-active-host");
    }

    inlineHost = host;
    const reveal = () => {
      inlineShowTimer = 0;
      if (!inlineHost || inlineHost !== host || !host.isConnected) return;
      ensureInlineClock();
      inlineHost.classList.add("bt-clock-active-host");
      if (inlineClock.hidden) inlineClock.hidden = false;
      inlineHost.appendChild(inlineClock);
      if (inlineAnimation) inlineAnimation.play();
    };

    if (immediate) reveal();
    else inlineShowTimer = window.setTimeout(reveal, 20);
  }

  function syncInlineClock() {
    window.clearTimeout(syncTimer);
    let candidate = null;

    const pendingAction = document.querySelector(
      '[data-bt-action-clock-host="true"], [data-bt-action-pending="true"]'
    );
    if (
      pendingAction &&
      !pendingAction.closest("[hidden]") &&
      pendingAction.getClientRects().length
    ) {
      candidate = pendingAction;
    }

    if (!candidate) {
      const candidates = document.querySelectorAll(busySelector);
      for (let index = 0; index < candidates.length; index += 1) {
        const item = candidates[index];
        if (!item.closest("[hidden]") && item.getClientRects().length) {
          candidate = item;
          break;
        }
      }
    }
    const host = resolveBusyHost(candidate);
    if (!host || !host.isConnected) {
      clearInlineClock();
      return;
    }

    showInlineClock(host);
  }

  function scheduleSync(delay) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncInlineClock, delay || 0);
  }

  function guardLoadMoreClick(event) {
    const button = event.target.closest && event.target.closest("a.hdt-loamore-btn");
    if (!button) return;

    const wrapper = button.closest("[loadmore-btn-wrapp]");
    if (
      button.dataset.btLoadPending === "true" ||
      (wrapper && wrapper.hasAttribute("loading"))
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    button.dataset.btLoadPending = "true";
    button.setAttribute("aria-disabled", "true");
    if (wrapper) wrapper.setAttribute("loading", "");
    showInlineClock(button, true);

    const safety = window.setTimeout(() => {
      delete button.dataset.btLoadPending;
      button.removeAttribute("aria-disabled");
      if (wrapper && wrapper.isConnected) wrapper.removeAttribute("loading");
      clearInlineClock();
      scheduleSync(0);
    }, 12000);
    loadMoreSafetyTimers.set(button, safety);
  }

  function finishLoadMore(event) {
    const products = event.target && event.target.closest
      ? event.target.closest(".bt-product-grid-scope")
      : null;
    const scope = products || (event.target && event.target.closest
      ? event.target.closest(".shopify-section")
      : null) || document;

    const wrappers = scope.querySelectorAll("[loadmore-btn-wrapp][loading]");
    const buttons = scope.querySelectorAll('a.hdt-loamore-btn[data-bt-load-pending="true"]');
    if (!wrappers.length && !buttons.length) return;

    wrappers.forEach((wrapper) => wrapper.removeAttribute("loading"));
    buttons.forEach((button) => {
      const safety = loadMoreSafetyTimers.get(button);
      if (safety) window.clearTimeout(safety);
      loadMoreSafetyTimers.delete(button);
      delete button.dataset.btLoadPending;
      button.removeAttribute("aria-disabled");
    });

    clearInlineClock();
    scheduleSync(0);
  }

  function resolveCardActionHost(button) {
    return button || null;
  }

  function findCardAction(event) {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    for (let index = 0; index < path.length; index += 1) {
      const item = path[index];
      if (item && item.matches && item.matches(cardActionSelector)) return item;
    }
    return event.target.closest && event.target.closest(cardActionSelector);
  }

  function finishCardAction(host, force) {
    if (!host) return;

    const startedAt = actionStartedAt.get(host);
    const elapsed = startedAt ? performance.now() - startedAt : 180;
    if (!force && elapsed < 180) {
      const previousFinish = actionFinishTimers.get(host);
      if (previousFinish) window.clearTimeout(previousFinish);
      const finishTimer = window.setTimeout(
        () => finishCardAction(host, true),
        Math.max(0, 180 - elapsed)
      );
      actionFinishTimers.set(host, finishTimer);
      return;
    }

    const pendingFinish = actionFinishTimers.get(host);
    if (pendingFinish) window.clearTimeout(pendingFinish);
    actionFinishTimers.delete(host);
    actionStartedAt.delete(host);

    const safety = actionSafetyTimers.get(host);
    if (safety) window.clearTimeout(safety);
    actionSafetyTimers.delete(host);

    const buttons = [];
    if (host.matches && host.matches('[data-bt-action-pending="true"]')) buttons.push(host);
    host.querySelectorAll?.('[data-bt-action-pending="true"]').forEach((button) => buttons.push(button));
    buttons.forEach((button) => {
      delete button.dataset.btActionPending;
      button.classList.remove("bt-search-action-pending");
    });

    delete host.dataset.btActionClockHost;
    delete host.dataset.btActionControl;
    host.classList.remove("bt-card-action-clock-host");
    if (inlineHost === host) clearInlineClock();
    scheduleSync(0);
  }

  function finishActionsForControl(controlId) {
    document.querySelectorAll('[data-bt-action-clock-host="true"]').forEach((host) => {
      if (!controlId || host.dataset.btActionControl === controlId) {
        finishCardAction(host);
      }
    });
  }

  function guardCardActionClick(event) {
    const button = findCardAction(event);
    if (!button || button.matches('[disabled], [aria-disabled="true"]')) return;
    if (button.tagName === "A" && !button.hasAttribute("aria-controls")) return;

    const host = resolveCardActionHost(button);
    if (!host || host.dataset.btActionClockHost === "true") return;

    const previous = actionSafetyTimers.get(host);
    if (previous) window.clearTimeout(previous);
    host.dataset.btActionClockHost = "true";
    host.dataset.btActionControl = button.getAttribute("aria-controls") || "";
    host.classList.add("bt-card-action-clock-host");
    button.dataset.btActionPending = "true";
    button.classList.add("bt-search-action-pending");
    actionStartedAt.set(host, performance.now());
    showInlineClock(host, true);

    const safety = window.setTimeout(() => finishCardAction(host, true), 9000);
    actionSafetyTimers.set(host, safety);
  }

  function initialize() {
    document.documentElement.classList.add("bt-clock-loader-ready");
    document.addEventListener("theme:loading:doing", showPageLoader);
    document.addEventListener("theme:loading:done", () => hidePageLoader(false));
    document.addEventListener("click", guardLoadMoreClick, true);
    document.addEventListener("click", guardCardActionClick, true);
    document.addEventListener("products:update", finishLoadMore);
    [
      "quick-view:loaded",
      "quick-add:loaded",
      "cart:update",
      "cart:drawer:render",
      "cart:main:render"
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => finishActionsForControl());
    });

    const observer = new MutationObserver((records) => {
      for (let index = 0; index < records.length; index += 1) {
        const target = records[index].target;
        const modal = target.closest && target.closest("#hdt-quick-view-modal, #hdt-quick-add-modal");
        if (modal && modal.hasAttribute("open")) {
          const inner = modal.querySelector(".hdt-dialog-modal__inner");
          if (inner && inner.children.length) {
            finishActionsForControl(modal.id);
            break;
          }
        }
        if (
          target === inlineHost ||
          (target.matches && target.matches(busySelector))
        ) {
          scheduleSync(0);
          break;
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-busy", "loading", "open"]
    });
  }

  initialize();
})();
